import { useState, useEffect, useMemo } from 'react'
import { vaultDb, type CodeVaultEntry } from '@/lib/supabase'
import { synapseWorkerBridge } from '@/lib/SynapseWorkerBridge'

const LANGUAGE_ICONS: Record<string, string> = {
  typescript: '🔷',
  javascript: '🟡',
  rust: '🦀',
  python: '🐍',
  css: '🎨',
  sql: '🗄️',
  html: '🌐',
  json: '📋',
  markdown: '📝',
  shell: '💻',
}

const TYPE_ICONS: Record<string, string> = {
  react_component: '🧩',
  hook: '🪝',
  store: '📦',
  utility: '🛠️',
  type: '📝',
  api: '🔌',
  style: '🎨',
  misc: '📁',
}

// Format large numbers: 1234567 -> "1.23M", 12345 -> "12.3K"
const formatNumber = (n: number): string => {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(2) + 'M'
  if (n >= 1_000) return (n / 1_000).toFixed(1) + 'K'
  return n.toLocaleString()
}

type SortOption = 'recent' | 'oldest' | 'alphabetical' | 'type' | 'size'

export function VaultBrowser() {
  const [entries, setEntries] = useState<CodeVaultEntry[]>([])
  const [categories, setCategories] = useState<{ component_type: string; count: number }[]>([])
  const [languages, setLanguages] = useState<{ language: string; count: number }[]>([])
  const [stats, setStats] = useState({ total: 0, languages: 0, types: 0, totalLines: 0 })
  const [selectedEntry, setSelectedEntry] = useState<CodeVaultEntry | null>(null)
  const [loading, setLoading] = useState(true)
  const [copying, setCopying] = useState(false)
  const [addingToLightGrid, setAddingToLightGrid] = useState(false)
  const [lightGridStatus, setLightGridStatus] = useState<{ success: boolean; message: string } | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [filterType, setFilterType] = useState<string | null>(null)
  const [filterLang, setFilterLang] = useState<string | null>(null)
  const [viewMode, setViewMode] = useState<'type' | 'language'>('type')
  const [page, setPage] = useState(0)
  const [hasMore, setHasMore] = useState(true)
  const [sortBy, setSortBy] = useState<SortOption>(() => {
    const saved = localStorage.getItem('vault-sort')
    return (saved as SortOption) || 'recent'
  })
  const PAGE_SIZE = 100

  useEffect(() => {
    loadCategories()
  }, [])

  // Persist sort preference
  useEffect(() => {
    localStorage.setItem('vault-sort', sortBy)
  }, [sortBy])

  // Load entries when filter changes
  useEffect(() => {
    if (filterType || filterLang) {
      setPage(0)
      setEntries([])
      loadEntries(0)
    } else {
      setEntries([])
    }
  }, [filterType, filterLang])

  // Sort entries client-side
  const sortedEntries = useMemo(() => {
    const sorted = [...entries]

    switch (sortBy) {
      case 'recent':
        return sorted.sort((a, b) => {
          const dateA = new Date(a.updated_at || a.created_at).getTime()
          const dateB = new Date(b.updated_at || b.created_at).getTime()
          return dateB - dateA
        })
      case 'oldest':
        return sorted.sort((a, b) => {
          const dateA = new Date(a.created_at).getTime()
          const dateB = new Date(b.created_at).getTime()
          return dateA - dateB
        })
      case 'alphabetical':
        return sorted.sort((a, b) => a.name.localeCompare(b.name))
      case 'type':
        return sorted.sort((a, b) => {
          const typeCompare = (a.component_type || '').localeCompare(b.component_type || '')
          if (typeCompare !== 0) return typeCompare
          return a.name.localeCompare(b.name)
        })
      case 'size':
        return sorted.sort((a, b) => b.size_bytes - a.size_bytes)
      default:
        return sorted
    }
  }, [entries, sortBy])

  const loadCategories = async () => {
    setLoading(true)
    const [categoriesData, languagesData, statsData] = await Promise.all([
      vaultDb.getCategories(),
      vaultDb.getLanguages(),
      vaultDb.getStats(),
    ])
    setCategories(categoriesData)
    setLanguages(languagesData)
    setStats(statsData)
    setLoading(false)
  }

  const loadEntries = async (pageNum: number) => {
    setLoading(true)
    let data: CodeVaultEntry[] = []

    if (filterType) {
      data = await vaultDb.getByType(filterType, PAGE_SIZE, pageNum * PAGE_SIZE)
    } else if (filterLang) {
      data = await vaultDb.getByLanguage(filterLang, PAGE_SIZE, pageNum * PAGE_SIZE)
    } else {
      data = await vaultDb.getAll(PAGE_SIZE, pageNum * PAGE_SIZE)
    }

    if (pageNum === 0) {
      setEntries(data)
    } else {
      setEntries(prev => [...prev, ...data])
    }
    setHasMore(data.length === PAGE_SIZE)
    setLoading(false)
  }

  const loadMore = () => {
    const nextPage = page + 1
    setPage(nextPage)
    loadEntries(nextPage)
  }

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setFilterType(null)
      setFilterLang(null)
      return
    }
    setLoading(true)
    const results = await vaultDb.search(searchQuery, 200)
    setEntries(results)
    setHasMore(false)
    setLoading(false)
  }


  const copyToClipboard = async () => {
    if (!selectedEntry) return
    setCopying(true)
    try {
      await navigator.clipboard.writeText(selectedEntry.content)
      setTimeout(() => setCopying(false), 1000)
    } catch {
      setCopying(false)
    }
  }

  const addToLightGrid = async () => {
    if (!selectedEntry) return
    setAddingToLightGrid(true)
    setLightGridStatus(null)
    try {
      const response = await synapseWorkerBridge.send<{ success: boolean; category?: string; error?: string }>(
        'lightgrid:add',
        {
          componentId: selectedEntry.name,
          config: {
            name: selectedEntry.name,
            content: selectedEntry.content,
            category: selectedEntry.component_type || 'ui',
            component_type: selectedEntry.component_type || 'react_component',
          },
        }
      )
      const success = response.type === 'response' && response.payload.success
      setLightGridStatus({
        success,
        message: success ? `✓ Added to ${selectedEntry.component_type || 'ui'}/` : 'Failed to add',
      })
      setTimeout(() => setLightGridStatus(null), 3000)
    } catch (err) {
      setLightGridStatus({ success: false, message: 'Network error' })
      setTimeout(() => setLightGridStatus(null), 3000)
    } finally {
      setAddingToLightGrid(false)
    }
  }

  if (loading && entries.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-400">Loading vault from Supabase...</div>
      </div>
    )
  }

  return (
    <div className="vault-browser grid grid-cols-1 lg:grid-cols-3 gap-4 h-full">
      {/* Stats Bar */}
      <div className="lg:col-span-3 bg-gradient-to-r from-cyan-900/20 to-purple-900/20 rounded-lg border border-cyan-600/30 p-3 flex items-center gap-6">
        <div className="flex items-center gap-2">
          <span className="text-2xl">🗄️</span>
          <div>
            <div className="text-xs text-gray-400">Total</div>
            <div className="text-lg font-bold text-cyan-400">{formatNumber(stats.total)}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-2xl">📊</span>
          <div>
            <div className="text-xs text-gray-400">Lines</div>
            <div className="text-lg font-bold text-purple-400">{formatNumber(stats.totalLines)}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-2xl">🏷️</span>
          <div>
            <div className="text-xs text-gray-400">Types</div>
            <div className="text-lg font-bold text-green-400">{stats.types}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-2xl">🌐</span>
          <div>
            <div className="text-xs text-gray-400">Languages</div>
            <div className="text-lg font-bold text-yellow-400">{stats.languages}</div>
          </div>
        </div>

        <div className="ml-auto flex items-center gap-2">
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as SortOption)}
            className="px-3 py-1.5 bg-[#0d1117] border border-[#30363d] rounded text-sm text-white cursor-pointer hover:border-cyan-500 transition"
            title="Sort entries"
          >
            <option value="recent">📅 Most Recent</option>
            <option value="oldest">📆 Oldest First</option>
            <option value="alphabetical">🔤 Alphabetical</option>
            <option value="type">🏷️ By Type</option>
            <option value="size">📊 By Size</option>
          </select>
          <input
            type="text"
            placeholder="Search vault..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            className="px-3 py-1.5 bg-[#0d1117] border border-[#30363d] rounded text-sm text-white placeholder-gray-500 w-48"
          />
          <button
            onClick={handleSearch}
            className="px-3 py-1.5 bg-cyan-600 hover:bg-cyan-700 text-white rounded text-sm"
          >
            Search
          </button>
          <button
            onClick={() => { setFilterType(null); setFilterLang(null); loadCategories() }}
            className="px-3 py-1.5 bg-[#21262d] hover:bg-[#30363d] text-gray-300 rounded text-sm"
          >
            ↻ Refresh
          </button>
        </div>
      </div>

      {/* Categories & Files */}
      <div className="lg:col-span-1 space-y-4 overflow-y-auto max-h-[70vh]">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-lg font-semibold text-white">Code Vault</h3>
          <div className="flex gap-1">
            <button
              onClick={() => setViewMode('type')}
              className={`px-2 py-1 text-xs rounded ${viewMode === 'type' ? 'bg-cyan-600 text-white' : 'bg-[#21262d] text-gray-400'}`}
            >
              By Type
            </button>
            <button
              onClick={() => setViewMode('language')}
              className={`px-2 py-1 text-xs rounded ${viewMode === 'language' ? 'bg-cyan-600 text-white' : 'bg-[#21262d] text-gray-400'}`}
            >
              By Lang
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 mb-2">
          {filterType && (
            <span className="px-2 py-1 bg-cyan-600/20 text-cyan-400 text-xs rounded flex items-center gap-1">
              Type: {filterType}
              <button onClick={() => setFilterType(null)} className="hover:text-white">×</button>
            </span>
          )}
          {filterLang && (
            <span className="px-2 py-1 bg-purple-600/20 text-purple-400 text-xs rounded flex items-center gap-1">
              Lang: {filterLang}
              <button onClick={() => setFilterLang(null)} className="hover:text-white">×</button>
            </span>
          )}
        </div>

        {/* Category buttons when no filter */}
        {!filterType && !filterLang && entries.length === 0 && (
          <div className="space-y-2">
            <h4 className="text-sm text-gray-400 mb-2">Browse by Type</h4>
            {categories.map(cat => (
              <button
                key={cat.component_type}
                onClick={() => setFilterType(cat.component_type)}
                className="w-full px-4 py-3 bg-[#161b22] rounded-lg border border-[#30363d] hover:border-cyan-500 transition flex items-center gap-3"
              >
                <span className="text-xl">{TYPE_ICONS[cat.component_type] || '📁'}</span>
                <span className="capitalize flex-1 text-left">{cat.component_type.replace('_', ' ')}</span>
                <span className="text-cyan-400 font-mono">{cat.count}</span>
              </button>
            ))}
            <h4 className="text-sm text-gray-400 mt-4 mb-2">Browse by Language</h4>
            {languages.slice(0, 8).map(lang => (
              <button
                key={lang.language}
                onClick={() => setFilterLang(lang.language)}
                className="w-full px-4 py-3 bg-[#161b22] rounded-lg border border-[#30363d] hover:border-purple-500 transition flex items-center gap-3"
              >
                <span className="text-xl">{LANGUAGE_ICONS[lang.language] || '📄'}</span>
                <span className="capitalize flex-1 text-left">{lang.language}</span>
                <span className="text-purple-400 font-mono">{lang.count}</span>
              </button>
            ))}
          </div>
        )}

        {/* File list when filtered */}
        {entries.length > 0 && (
          <div className="bg-[#161b22] rounded-lg border border-[#30363d] overflow-hidden">
            <div className="divide-y divide-[#30363d] max-h-[60vh] overflow-y-auto">
              {sortedEntries.map((entry) => (
                <button
                  key={entry.id}
                  onClick={() => setSelectedEntry(entry)}
                  className={`w-full px-4 py-2 text-left text-sm hover:bg-[#21262d] transition flex items-center gap-2 ${
                    selectedEntry?.id === entry.id ? 'bg-cyan-500/10 text-cyan-400' : 'text-gray-300'
                  }`}
                >
                  <span className="text-gray-500">{LANGUAGE_ICONS[entry.language] || '📄'}</span>
                  <span className="truncate flex-1">{entry.name}</span>
                  <span className="text-xs text-gray-600 ml-auto">
                    {entry.lines_of_code}L
                  </span>
                </button>
              ))}
            </div>
            {hasMore && (
              <button
                onClick={loadMore}
                disabled={loading}
                className="w-full px-4 py-3 bg-[#21262d] hover:bg-[#30363d] text-cyan-400 text-sm transition border-t border-[#30363d]"
              >
                {loading ? 'Loading...' : `Load More (${sortedEntries.length} loaded)`}
              </button>
            )}
          </div>
        )}
      </div>

      {/* File Preview */}
      <div className="lg:col-span-2 bg-[#161b22] rounded-lg border border-[#30363d] overflow-hidden flex flex-col">
        <div className="px-4 py-3 border-b border-[#30363d] flex items-center justify-between">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <span className="text-gray-500">{selectedEntry ? (LANGUAGE_ICONS[selectedEntry.language] || '📄') : '📄'}</span>
            <span className="font-mono text-sm text-gray-300 truncate">
              {selectedEntry?.name || 'Select a file'}
            </span>
            {selectedEntry && (
              <>
                <span className="px-2 py-0.5 bg-[#21262d] text-gray-400 text-xs rounded">
                  {selectedEntry.language}
                </span>
                <span className="px-2 py-0.5 bg-cyan-600/20 text-cyan-400 text-xs rounded">
                  {selectedEntry.component_type}
                </span>
              </>
            )}
          </div>
          {selectedEntry && (
            <div className="flex gap-2">
              <button
                onClick={copyToClipboard}
                className={`px-3 py-1 rounded text-sm transition ${
                  copying
                    ? 'bg-green-600 text-white'
                    : 'bg-[#21262d] text-gray-300 hover:bg-[#30363d]'
                }`}
              >
                {copying ? '✓ Copied' : '📋 Copy'}
              </button>
              <button
                onClick={() => {
                  // Download file as .tsx/.ts/.js etc
                  const ext = selectedEntry.language === 'typescript' ? 'tsx' :
                              selectedEntry.language === 'javascript' ? 'js' :
                              selectedEntry.language === 'rust' ? 'rs' :
                              selectedEntry.language === 'python' ? 'py' :
                              selectedEntry.language === 'css' ? 'css' :
                              selectedEntry.language === 'sql' ? 'sql' :
                              selectedEntry.language === 'json' ? 'json' : 'txt';
                  const filename = selectedEntry.name.endsWith(`.${ext}`) ? selectedEntry.name : `${selectedEntry.name}.${ext}`;
                  const blob = new Blob([selectedEntry.content], { type: 'text/plain' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = filename;
                  a.click();
                  URL.revokeObjectURL(url);
                }}
                className="px-3 py-1 rounded text-sm bg-green-600/20 text-green-400 hover:bg-green-600/30 transition"
              >
                📥 Export
              </button>
              <button
                onClick={() => {
                  // Navigate to LightGrid with this component pre-selected
                  const params = new URLSearchParams({
                    template: selectedEntry.component_type || 'ui',
                    name: selectedEntry.name,
                    code: encodeURIComponent(selectedEntry.content.slice(0, 5000)) // Limit size for URL
                  });
                  window.location.href = `/dashboard/builder?${params.toString()}`;
                }}
                className="px-3 py-1 rounded text-sm bg-purple-600/20 text-purple-400 hover:bg-purple-600/30 transition"
                title="Use this component in the visual builder (one-time)"
              >
                🎨 Use in Builder
              </button>
              <button
                onClick={addToLightGrid}
                disabled={addingToLightGrid}
                className={`px-3 py-1 rounded text-sm transition ${
                  lightGridStatus?.success
                    ? 'bg-green-600/30 text-green-400'
                    : lightGridStatus && !lightGridStatus.success
                    ? 'bg-red-600/30 text-red-400'
                    : 'bg-cyan-600/20 text-cyan-400 hover:bg-cyan-600/30'
                } ${addingToLightGrid ? 'opacity-50 cursor-wait' : ''}`}
                title="Permanently add this component to the LightGrid library"
              >
                {addingToLightGrid ? '⏳ Adding...' : lightGridStatus ? lightGridStatus.message : '📦 Add to LightGrid'}
              </button>
            </div>
          )}
        </div>

        {/* Entry details */}
        {selectedEntry && (
          <div className="px-4 py-2 bg-[#0d1117] border-b border-[#30363d] text-xs flex flex-wrap gap-4">
            <span className="text-gray-400">
              <span className="text-gray-500">Lines:</span> {selectedEntry.lines_of_code}
            </span>
            <span className="text-gray-400">
              <span className="text-gray-500">Size:</span> {(selectedEntry.size_bytes / 1024).toFixed(1)}KB
            </span>
            {selectedEntry.file_path && (
              <span className="text-gray-400 truncate max-w-md" title={selectedEntry.file_path}>
                <span className="text-gray-500">Source:</span> {selectedEntry.file_path.split('/').slice(-3).join('/')}
              </span>
            )}
            {selectedEntry.tags?.length > 0 && (
              <span className="text-gray-400">
                <span className="text-gray-500">Tags:</span> {selectedEntry.tags.join(', ')}
              </span>
            )}
          </div>
        )}

        <div className="flex-1 overflow-auto p-4">
          {selectedEntry ? (
            <pre className="text-sm font-mono text-gray-300 whitespace-pre-wrap break-words">
              {selectedEntry.content}
            </pre>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-gray-500">
              <div className="text-4xl mb-4">🗄️</div>
              <div>Select a file from the vault to preview</div>
              <div className="text-sm mt-2 text-gray-600">
                {stats.total.toLocaleString()} entries indexed from across the VPS
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
